import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import type { SelectChangeEvent } from "@mui/material";
import {
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment } from "react";
import type { ClientProfile, ClientTypeFilter, SearchResultGroup } from "./clientProfilesTypes";

const clientTypeFilters: ClientTypeFilter[] = ["All", "FundAdmin", "FundManager"];

const isClientTypeFilter = (value: string): value is ClientTypeFilter => {
  return value === "All" || value === "FundAdmin" || value === "FundManager";
};

interface Props {
  searchTerm: string;
  clientTypeFilter: ClientTypeFilter;
  organizationScope: string;
  organizations: ClientProfile[];
  filteredClients: ClientProfile[];
  groupedFilteredClients: SearchResultGroup[];
  selectedClientCode: string;
  onSearchTermChange: (value: string) => void;
  onClientTypeFilterChange: (value: ClientTypeFilter) => void;
  onOrganizationScopeChange: (value: string) => void;
  onSelectClient: (clientCode: string) => void;
}

interface ClientRowProps {
  client: ClientProfile;
  selected: boolean;
  indented?: boolean;
  highlightAsGroup?: boolean;
  onSelect: (clientCode: string) => void;
}

const ClientRow = ({ client, selected, indented, highlightAsGroup, onSelect }: ClientRowProps) => {
  return (
    <ListItem disablePadding>
      <ListItemButton
        dense
        selected={selected}
        onClick={() => onSelect(client.clientCode)}
        sx={{
          minHeight: "1.9rem",
          py: 0.25,
          pl: indented ? 5 : 1,
          pr: 1,
          borderRadius: 0.5,
          "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "action.selected" },
        }}
      >
        <ListItemIcon sx={{ minWidth: "1.8rem" }}>
          {client.type === "FundAdmin" ? <ApartmentRoundedIcon /> : <BusinessRoundedIcon />}
        </ListItemIcon>
        <ListItemText
          primary={`${client.title} (${client.clientCode})`}
          slotProps={{
            primary: {
              noWrap: true,
              sx: { fontWeight: highlightAsGroup ? "bold" : "normal" },
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const ClientProfilesSelectorPanel = ({
  searchTerm,
  clientTypeFilter,
  organizationScope,
  organizations,
  filteredClients,
  groupedFilteredClients,
  selectedClientCode,
  onSearchTermChange,
  onClientTypeFilterChange,
  onOrganizationScopeChange,
  onSelectClient,
}: Props) => {
  const handleClientTypeFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (!isClientTypeFilter(value)) {
      return;
    }

    onClientTypeFilterChange(value);
  };

  return (
    <Grid
      container
      columns={2}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        height: "clamp(11rem, calc(100vh - 48rem), 14rem)",
        overflow: "hidden",
      })}
    >
      <Grid
        size="auto"
        sx={[
          {
            width: "16rem",
            flexShrink: 0,
            p: 1.5,
          },
          (theme) => ({
            borderRight: `1px solid ${theme.palette.divider}`,
          }),
        ]}
      >
        <Stack spacing={2}>
          <TextField
            label="Search by client name or code"
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="client-profile-type-filter-label">Client type</InputLabel>
            <Select
              labelId="client-profile-type-filter-label"
              label="Client type"
              value={clientTypeFilter}
              onChange={handleClientTypeFilterChange}
            >
              {clientTypeFilters.map((typeFilter) => (
                <MenuItem key={typeFilter} value={typeFilter}>
                  {typeFilter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="client-profile-org-filter-label">Organization scope (optional)</InputLabel>
            <Select
              labelId="client-profile-org-filter-label"
              label="Organization scope (optional)"
              value={organizationScope}
              onChange={(event) => onOrganizationScopeChange(event.target.value)}
            >
              <MenuItem value="">All organizations</MenuItem>
              {organizations.map((organization) => (
                <MenuItem key={organization.clientCode} value={organization.clientCode}>
                  {organization.title} ({organization.clientCode})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Grid>
      <Grid size="grow" sx={{ p: 1.5, minWidth: 0, minHeight: 0, height: "100%", overflow: "hidden", py: 2 }}>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Typography variant="subtitle2">Matching Clients</Typography>

          {filteredClients.length === 0 ? (
            <Typography color="textSecondary">No clients match current filters.</Typography>
          ) : (
            <List
              dense
              sx={{
                overflowY: "scroll",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                scrollbarGutter: "stable",
                minHeight: 0,
                flex: 1,
                p: 0,
                pr: 0.5,
              }}
            >
              {organizationScope
                ? filteredClients.map((client) => (
                    <ClientRow
                      key={client.clientCode}
                      client={client}
                      selected={selectedClientCode === client.clientCode}
                      onSelect={onSelectClient}
                    />
                  ))
                : groupedFilteredClients.map((group) => (
                    <Fragment key={group.key}>
                      {group.organization ? (
                        group.organizationMatchesFilter ? (
                          <ClientRow
                            key={group.organization.clientCode}
                            client={group.organization}
                            selected={selectedClientCode === group.organization.clientCode}
                            highlightAsGroup
                            onSelect={onSelectClient}
                          />
                        ) : (
                          <ListItem key={group.organization.clientCode} sx={{ minHeight: "1.9rem", py: 0.25, px: 1 }}>
                            <ListItemIcon sx={{ minWidth: "1.8rem" }}>
                              <ApartmentRoundedIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${group.organization.title} (${group.organization.clientCode})`}
                              slotProps={{
                                primary: {
                                  noWrap: true,
                                  color: "textSecondary",
                                  sx: { fontWeight: "bold" },
                                },
                              }}
                            />
                          </ListItem>
                        )
                      ) : (
                        <ListItem sx={{ px: 1, py: 0.5 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {group.label}
                          </Typography>
                        </ListItem>
                      )}
                      {group.clients.map((client) => (
                        <ClientRow
                          key={client.clientCode}
                          client={client}
                          selected={selectedClientCode === client.clientCode}
                          indented
                          onSelect={onSelectClient}
                        />
                      ))}
                    </Fragment>
                  ))}
            </List>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ClientProfilesSelectorPanel;
